import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, GraduationCap, ExternalLink, Trash2, AlertCircle, Loader2 } from "lucide-react";
import { useCompletedCourses } from "@/hooks/useCompletedCourses";
import { toast } from "@/hooks/use-toast";

export default function AlunoCursos() {
  const navigate = useNavigate();
  const { courses, isLoading, deleteCourse } = useCompletedCourses();

  const handleDelete = async (id: string) => {
    try {
      await deleteCourse.mutateAsync(id);
      toast({ title: "Curso removido" });
    } catch {
      toast({ title: "Erro ao remover", variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/experiencia")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground">Cursos Realizados</h1>
          <p className="text-muted-foreground text-sm">Cursos e formações que você completou</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-success/10 rounded-xl p-4 mb-6 border border-success/20 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-success shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-foreground">Sem upload de certificados</p>
          <p className="text-xs text-muted-foreground mt-1">Basta informar o curso. Valorizamos sua trajetória.</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
        <Button variant="hero" size="lg" className="w-full" onClick={() => navigate("/aluno/experiencia/cursos/novo")}>
          <Plus className="w-5 h-5" />Adicionar curso
        </Button>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-3">
        {courses.length > 0 ? (
          courses.map((curso) => (
            <div key={curso.id} className="bg-card rounded-xl p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
                  <GraduationCap className="w-5 h-5 text-success" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{curso.course_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {[curso.provider, curso.year_label].filter(Boolean).join(" • ")}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {curso.url && (
                    <Button variant="ghost" size="icon-sm" asChild>
                      <a href={curso.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a>
                    </Button>
                  )}
                  <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(curso.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-muted/50 rounded-2xl p-8 text-center">
            <GraduationCap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Nenhum curso adicionado ainda</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
